import React, { Suspense, useEffect, useId, useMemo, useState } from 'react'
import { FleetSection, Notice, RollupTiles } from '../../../../../shared/sso-workspace/fleet-page'
import { serverMessage } from '../../../../../shared/error/server-message'
import { ErrorReference } from '../../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../../shared/error/use-error-line'
import { formatCount } from '../../../../../shared/sso-workspace/format'
import { addDays, dayOf, dayWindow, viewerTimeZone, zoneName } from '../../../../../shared/date/local-time'
import { getKeyUsage } from '../api'
import { UNDER_COUNT_NOTE } from '../key-words'

// ECharts in its own chunk, loaded when there is something to draw.
const ApiKeyUsageChart = React.lazy(() => import('./api-key-usage-chart').then(m => ({ default: m.ApiKeyUsageChart })))

// A key's usage (Task 15; ruling 10): requests per day, stacked by outcome,
// in the reader's own days. Content's rollup keeps UTC hours, so the page
// asks for its days' instants by the hour and sums each hour into the day
// it falls on here: a day is the sum of its hours, 23 or 25 across a
// daylight-saving change.
//
// The request log drops requests when it is overloaded (a full queue, a
// failed batch), so usage can only under-count, never over-count: the page
// says so.

export const OUTCOMES = [
  { outcome: '2xx', label: 'Succeeded' },
  { outcome: '4xx', label: 'Refused' },
  { outcome: '429', label: 'Rate limited' },
  { outcome: '5xx', label: 'Server errors' }
]

export const PERIODS = [
  { days: 7, label: 'Last 7 days' },
  { days: 30, label: 'Last 30 days' },
  { days: 90, label: 'Last 90 days' }
]

// The hours of GET /api-key/:id/usage?group=hour ({ items: [{ hour,
// requests, outcomes: { 2xx, 4xx, 429, 5xx }, ... }], total, ... }), as
// { at, outcomes }.
export const usageRows = body => {
  const items = body && Array.isArray(body.items) ? body.items : []
  return items
    .filter(item => item && typeof item === 'object' && item.hour)
    .map(item => {
      const outcomes = item.outcomes || {}
      return { at: item.hour, outcomes: Object.fromEntries(OUTCOMES.map(o => [o.outcome, Number(outcomes[o.outcome]) || 0])) }
    })
}

// The hours summed into each of `days` (YYYY-MM-DD in tz), one series per
// outcome, in OUTCOMES' order.
export const dailyByOutcome = (body, days, tz = viewerTimeZone()) => {
  const index = new Map(days.map((day, i) => [day, i]))
  const series = OUTCOMES.map(o => ({ ...o, data: days.map(() => 0) }))
  usageRows(body).forEach(row => {
    const i = index.get(dayOf(row.at, tz))
    if (i === undefined) return
    series.forEach(s => { s.data[i] += row.outcomes[s.outcome] })
  })
  return series
}

// The last n days of the reader's calendar, today included.
const lastDays = n => {
  const today = dayOf(Date.now())
  return [...Array(n)].map((_, i) => addDays(today, i - n + 1))
}

export const ApiKeyUsage = ({ keyID }) => {
  const periodID = useId()
  const [days, setDays] = useState(30)
  const [rows, setRows] = useState(undefined)
  const [error, failure, setError] = useErrorLine()

  const span = useMemo(() => {
    const list = lastDays(days)
    return { list, from: dayWindow(list[0]).from, to: dayWindow(list[list.length - 1]).to }
  }, [days])

  useEffect(() => {
    let live = true
    setRows(undefined)
    getKeyUsage(keyID, { from: span.from, to: span.to }).then(
      body => { if (live) { setError(null); setRows(body || {}) } },
      err => { if (live) { setRows(null); setError(serverMessage(err, "This key's usage couldn't be loaded."), err) } }
    )
    return () => { live = false }
  }, [keyID, span, setError])

  const series = useMemo(() => (rows ? dailyByOutcome(rows, span.list) : []), [rows, span])
  const totals = useMemo(() => Object.fromEntries(series.map(s => [s.outcome, s.data.reduce((a, b) => a + b, 0)])), [series])
  const total = Object.values(totals).reduce((a, b) => a + b, 0)
  const period = PERIODS.find(p => p.days === days) || PERIODS[1]

  return (
    <FleetSection id="ak-usage" title="Usage">
      <div className="ak-filters">
        <div className="ak-filter">
          <label className="sw-label" htmlFor={periodID}>Period</label>
          <select id={periodID} className="form-select" value={String(days)} onChange={e => setDays(Number(e.target.value))}>
            {PERIODS.map(p => <option key={p.days} value={String(p.days)}>{p.label}</option>)}
          </select>
        </div>
      </div>

      {error && <Notice tone="bad" role="alert">{error}<ErrorReference error={failure} /></Notice>}
      {rows === undefined && !error && <p className="sw-hint" role="status">Loading…</p>}

      {rows && total === 0 && <p className="sw-empty-state">No requests in this period.</p>}
      {rows && total > 0 &&
        <>
          <RollupTiles label="Totals" tiles={[
            { key: 'total', label: 'Requests', value: formatCount(total) },
            ...series.map(s => ({ key: s.outcome, label: s.label, value: formatCount(totals[s.outcome]), tone: s.outcome === '2xx' ? 'ok' : s.outcome === '5xx' ? 'bad' : totals[s.outcome] ? 'warn' : undefined }))
          ]} />
          <Suspense fallback={<p className="sw-hint" role="status">Loading the chart…</p>}>
            <ApiKeyUsageChart days={span.list} series={series} label={`Requests per day, ${period.label.toLowerCase()}, by outcome`} />
          </Suspense>
          <p className="sw-hint small mt-2 mb-0">{`Days are in ${zoneName()}.`}</p>
        </>}

      <p className="sw-hint small mt-2 mb-0">{UNDER_COUNT_NOTE}</p>
    </FleetSection>
  )
}
